// mobile/screens/DashboardScreen.js
import React, {
  useEffect,
  useState,
  useContext,
  useLayoutEffect,
  useRef,
  useMemo,
} from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  RefreshControl,
  ScrollView,
  Alert,
  ActivityIndicator,
  Platform,
  AppState,
  Modal,
} from 'react-native';
import axios from 'axios';
import { API_BASE } from '../utils';
import { AuthContext } from '../App';
import { useFocusEffect } from '@react-navigation/native';
import PadiProIcon from '../components/PadiProIcon';
import EnterpriseHeader from '../components/EnterpriseHeader';
import { PADIPRO, RADIUS, SPACE, padiproShadow, padiproShadowMd, padiproShadowLg } from '../components/padiproTheme';
import { useResponsive } from '../components/responsive';

const VIOLET = '#6B5B8C';

const AUTO_REFRESH_MS = 15000;

// BMKG – Durian, Pantai Labu, Deli Serdang (adm4 default)
const DEFAULT_ADM4 = '12.07.32.2018';
const DEFAULT_LOC_LABEL = 'Durian';
const DEFAULT_LAT = 3.6564360977;
const DEFAULT_LON = 98.8521723097;
const FORECAST_DAYS = 14;
const EXTREME_RAIN_MM_PER_SLOT = 20;
const EXTREME_WIND_KMH = 20;
const WEATHER_ALERT_HORIZON_HOURS = 48;
const DAILY_WARN_RAIN_MM = 8;
const DAILY_EXTREME_RAIN_MM = 20;
const DAILY_WARN_GUST_KMH = 32;
const DAILY_EXTREME_GUST_KMH = 45;
const DAILY_WARN_WIND_KMH = 18;
const DAILY_EXTREME_WIND_KMH = 25;

// prefer “jam nyaman semprot”
const PREFERRED_WINDOWS = [
  { name: 'Pagi', start: 5, end: 9 }, // 05–09
  { name: 'Sore', start: 15, end: 18 }, // 15–18
];

const pad2 = (n) => (Number(n) < 10 ? `0${n}` : `${n}`);
const fmtTime = (ms) => {
  const d = new Date(ms);
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};
const fmtDayKey = (ms) => {
  const d = new Date(ms);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};
const dayLabel = (ms) =>
  new Date(ms).toLocaleDateString('id-ID', {
    weekday: 'long',
    day: '2-digit',
    month: 'short',
  });
const shortDayLabel = (ms) =>
  new Date(ms).toLocaleDateString('id-ID', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
  });

/* ===================== BMKG helpers ===================== */

function asNumber(val) {
  if (val === null || val === undefined || val === '') return null;
  if (typeof val === 'number') return Number.isFinite(val) ? val : null;
  const m = String(val).replace(',', '.').match(/-?\d+(\.\d+)?/);
  if (!m) return null;
  const n = Number(m[0]);
  return Number.isFinite(n) ? n : null;
}

function isRainDesc(desc = '') {
  return /hujan|gerimis|petir|badai|thunder|storm|rain|shower/i.test(String(desc || ''));
}

function isSevereRainDesc(desc = '') {
  return /hujan\s*(lebat|sangat\s*lebat|ekstrem)|badai|petir|thunder|storm/i.test(String(desc || ''));
}

function isWetSlot(slot = {}) {
  if (!slot) return false;
  if (isRainDesc(slot?.desc)) return true;
  const precipMm = asNumber(slot?.precipMm);
  return precipMm != null && precipMm > 0.15;
}

function flattenCuaca(cuaca) {
  if (!cuaca) return [];
  if (Array.isArray(cuaca) && cuaca.length && Array.isArray(cuaca[0])) return cuaca.flat().filter(Boolean);
  if (Array.isArray(cuaca)) return cuaca.filter(Boolean);
  return [];
}

function normalizeBmkgSlots(payload) {
  const root = payload?.data ?? payload;
  const area = Array.isArray(root) ? root?.[0] : root?.[0] || root;

  const raw = flattenCuaca(area?.cuaca || area?.data?.cuaca || area?.forecast?.cuaca);

  return raw
    .map((x) => {
      const dt =
        x?.local_datetime ||
        x?.datetime ||
        x?.jamCuaca ||
        x?.time ||
        x?.validtime ||
        x?.utc_datetime;

      const ms = dt ? Date.parse(dt) : NaN;
      if (!Number.isFinite(ms)) return null;

      const desc =
        x?.weather_desc ||
        x?.cuaca ||
        x?.weather ||
        x?.kondisi ||
        x?.desc ||
        '—';

      const tempC = asNumber(x?.t ?? x?.temp ?? x?.temperature ?? x?.tempC);
      const hum = asNumber(x?.hu ?? x?.humidity ?? x?.rh);
      const windKmh = asNumber(x?.ws ?? x?.wind_speed ?? x?.windSpeed ?? x?.wind_kmh);
      const precipMm = asNumber(x?.tp ?? x?.precipitation ?? x?.precip_mm ?? x?.rain_mm ?? x?.rain);

      return {
        ms,
        desc: String(desc),
        tempC: Number.isFinite(tempC) ? tempC : null,
        hum: Number.isFinite(hum) ? hum : null,
        windKmh: Number.isFinite(windKmh) ? windKmh : null,
        precipMm: Number.isFinite(precipMm) ? precipMm : null,
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.ms - b.ms);
}

function describeWeatherCode(code) {
  const c = asNumber(code);
  if (c == null) return 'Prakiraan';
  if (c === 0) return 'Cerah';
  if (c === 1 || c === 2) return 'Cerah berawan';
  if (c === 3) return 'Berawan';
  if (c === 45 || c === 48) return 'Berkabut';
  if (c >= 51 && c <= 57) return 'Gerimis';
  if (c >= 61 && c <= 67) return c >= 65 ? 'Hujan lebat' : 'Hujan';
  if (c >= 80 && c <= 82) return c >= 82 ? 'Hujan deras' : 'Hujan lokal';
  if (c >= 95) return 'Badai petir';
  return 'Prakiraan';
}

function isThunderWeatherCode(code) {
  const c = asNumber(code);
  return c != null && c >= 95;
}

function getForecastCoords(user = {}) {
  const lat = asNumber(user?.weather_lat ?? user?.lat ?? user?.latitude);
  const lon = asNumber(user?.weather_lon ?? user?.lon ?? user?.longitude);
  return {
    lat: lat ?? DEFAULT_LAT,
    lon: lon ?? DEFAULT_LON,
  };
}

function classifyDailyForecast(day = {}) {
  const reasons = [];
  const rainMm = asNumber(day?.rainMm);
  const rainProb = asNumber(day?.rainProb);
  const windKmh = asNumber(day?.windKmh);
  const gustKmh = asNumber(day?.gustKmh);

  if (isThunderWeatherCode(day?.weatherCode)) reasons.push('petir/badai');
  if (rainMm != null && rainMm >= DAILY_EXTREME_RAIN_MM) reasons.push(`hujan ${rainMm} mm`);
  if (gustKmh != null && gustKmh >= DAILY_EXTREME_GUST_KMH) reasons.push(`hembusan ${gustKmh} km/j`);
  if (windKmh != null && windKmh >= DAILY_EXTREME_WIND_KMH) reasons.push(`angin ${windKmh} km/j`);

  if (reasons.length) {
    return { level: 'HOLD', label: reasons.join(' + ') };
  }

  if (rainMm != null && rainMm >= DAILY_WARN_RAIN_MM) reasons.push(`hujan ${rainMm} mm`);
  if (rainProb != null && rainProb >= 70) reasons.push(`peluang hujan ${rainProb}%`);
  if (gustKmh != null && gustKmh >= DAILY_WARN_GUST_KMH) reasons.push(`hembusan ${gustKmh} km/j`);
  if (windKmh != null && windKmh >= DAILY_WARN_WIND_KMH) reasons.push(`angin ${windKmh} km/j`);

  if (reasons.length) {
    return { level: 'WARN', label: reasons.slice(0, 2).join(' + ') };
  }

  return { level: 'OK', label: 'Relatif stabil' };
}

function normalizeDailyForecast(payload, nowMs = Date.now()) {
  const daily = payload?.daily || {};
  const times = Array.isArray(daily.time) ? daily.time : [];
  const todayKey = fmtDayKey(nowMs);

  return times
    .map((dateKey, i) => {
      if (!dateKey || dateKey < todayKey) return null;
      const ms = Date.parse(`${dateKey}T12:00:00+07:00`);
      if (!Number.isFinite(ms)) return null;

      const weatherCode = asNumber(daily.weather_code?.[i]);
      const day = {
        dateKey,
        ms,
        weatherCode,
        desc: describeWeatherCode(weatherCode),
        rainMm: asNumber(daily.precipitation_sum?.[i]),
        rainProb: asNumber(daily.precipitation_probability_max?.[i]),
        windKmh: asNumber(daily.wind_speed_10m_max?.[i]),
        gustKmh: asNumber(daily.wind_gusts_10m_max?.[i]),
      };

      return {
        ...day,
        risk: classifyDailyForecast(day),
      };
    })
    .filter(Boolean)
    .slice(0, FORECAST_DAYS);
}

function classifyExtremeWeatherSlot(slot = {}) {
  const reasons = [];
  const precipMm = asNumber(slot?.precipMm);
  const windKmh = asNumber(slot?.windKmh);

  if (isSevereRainDesc(slot?.desc)) reasons.push('hujan lebat/petir');
  if (precipMm != null && precipMm >= EXTREME_RAIN_MM_PER_SLOT) {
    reasons.push(`curah hujan ${precipMm} mm`);
  }
  if (windKmh != null && windKmh >= EXTREME_WIND_KMH) {
    reasons.push(`angin ${windKmh} km/j`);
  }

  if (!reasons.length) return null;
  return {
    reasons,
    level: 'HOLD',
    label: reasons.join(' + '),
  };
}

function buildExtremeWeatherAlert(slots = [], nowMs = Date.now()) {
  const fromMs = nowMs - 30 * 60000;
  const toMs = nowMs + WEATHER_ALERT_HORIZON_HOURS * 3600000;
  const hits = slots
    .filter((slot) => slot.ms >= fromMs && slot.ms <= toMs)
    .map((slot) => ({ slot, extreme: classifyExtremeWeatherSlot(slot) }))
    .filter((x) => x.extreme)
    .sort((a, b) => a.slot.ms - b.slot.ms);

  if (!hits.length) return null;

  const first = hits[0];
  const slot = first.slot;
  const timeText = `${dayLabel(slot.ms)} ${fmtTime(slot.ms)}`;
  return {
    key: `${fmtDayKey(slot.ms)}-${fmtTime(slot.ms)}-${first.extreme.label}`,
    title: 'Peringatan Cuaca Ekstrem',
    headline: 'Waspada hujan/angin ekstrem',
    shortText: `${timeText}: ${first.extreme.label}`,
    message: `${timeText}: ${first.extreme.label}. Padi fase keluar malai rawan rebah, tunda aktivitas semprot/pupuk daun dan jaga air sawah tetap dangkal.`,
    hits,
  };
}

function buildDailyForecastAlert(days = []) {
  const hits = days
    .filter((day) => day?.risk?.level === 'HOLD')
    .sort((a, b) => a.ms - b.ms);

  if (!hits.length) return null;

  const first = hits[0];
  const timeText = dayLabel(first.ms);
  return {
    key: `daily-${first.dateKey}-${first.risk.label}`,
    title: 'Peringatan Prakiraan 14 Hari',
    headline: 'Waspada cuaca ekstrem 14 hari',
    shortText: `${timeText}: ${first.risk.label}`,
    message: `${timeText}: ${first.risk.label}. Pantau ulang mendekati hari H, terutama padi fase keluar malai karena rawan rebah saat hujan/petir dan angin kencang.`,
    hits,
  };
}

function slotSuitability(desc = '', windKmh) {
  const slot = typeof desc === 'object' ? (desc || {}) : { desc, windKmh };
  const extreme = classifyExtremeWeatherSlot(slot);
  if (extreme) return { level: 'HOLD', score: 0, reason: extreme.label };
  if (isWetSlot(slot)) return { level: 'HOLD', score: 0, reason: 'Hujan/petir' };

  const w = asNumber(slot.windKmh);
  if (Number.isFinite(w)) {
    if (w > 18) return { level: 'HOLD', score: 0, reason: 'Angin kencang' };
    if (w < 3) return { level: 'WARN', score: 1, reason: 'Angin sangat pelan' };
  }
  return { level: 'OK', score: 2, reason: 'Aman' };
}

// Rule tambahan: tunda kalau ada hujan 3–6 jam setelah semprot
function willRainWithin(slotsOfDay, startMs, fromH = 3, toH = 6) {
  const fromMs = startMs + fromH * 3600000;
  const toMs = startMs + toH * 3600000;
  return slotsOfDay.some((s) => s.ms >= fromMs && s.ms <= toMs && isWetSlot(s));
}

function buildGroups(enriched) {
  const groups = [];
  let cur = [];

  for (let i = 0; i < enriched.length; i++) {
    const s = enriched[i];
    const okish = s.suit.score >= 1; // WARN/OK
    if (!okish) {
      if (cur.length) groups.push(cur);
      cur = [];
      continue;
    }

    if (!cur.length) cur.push(s);
    else {
      const prev = cur[cur.length - 1];
      const diffH = (s.ms - prev.ms) / 3600000;
      if (diffH <= 4) cur.push(s);
      else {
        groups.push(cur);
        cur = [s];
      }
    }
  }
  if (cur.length) groups.push(cur);
  return groups;
}

// pilih window terbaik untuk 1 hari (min 6 jam = 2 slot)
function pickBestWindowForDay(slots, dKey) {
  const daySlots = slots.filter((s) => fmtDayKey(s.ms) === dKey);
  if (!daySlots.length) return null;

  const enriched = daySlots.map((s) => ({ ...s, suit: slotSuitability(s) }));
  const groups = buildGroups(enriched);
  const candidates = groups.filter((g) => g.length >= 2);

  // fallback: best single slot (tapi tetap cek hujan 3–6 jam setelah)
  if (!candidates.length) {
    const best = enriched.slice().sort((a, b) => b.suit.score - a.suit.score)[0];
    const rainAfter = willRainWithin(enriched, best.ms, 3, 6);

    const endMs = best.ms + 3 * 3600000;
    if (rainAfter) {
      return {
        kind: 'single',
        level: 'HOLD',
        startMs: best.ms,
        endMs,
        windowText: `${fmtTime(best.ms)}–${fmtTime(endMs)}`,
        reason: 'Tunda: ada hujan 3–6 jam setelah semprot',
        preferredName: null,
      };
    }

    return {
      kind: 'single',
      level: best.suit.level,
      startMs: best.ms,
      endMs,
      windowText: `${fmtTime(best.ms)}–${fmtTime(endMs)}`,
      reason: best.suit.reason,
      preferredName: null,
    };
  }

  const scored = candidates.map((g) => {
    // cari start pertama yang lolos rainfast
    let safeStart = null;
    for (let i = 0; i < g.length; i++) {
      if (g[i].suit.score < 1) continue;
      if (!willRainWithin(enriched, g[i].ms, 3, 6)) {
        safeStart = g[i];
        break;
      }
    }

    const startMs = safeStart ? safeStart.ms : g[0].ms;
    const endMs = startMs + 6 * 3600000;

    if (!safeStart) {
      return {
        kind: 'window',
        level: 'HOLD',
        startMs,
        endMs,
        windowText: `${fmtTime(startMs)}–${fmtTime(endMs)}`,
        score: -999,
        reason: 'Tunda: ada hujan 3–6 jam setelah semprot',
        preferredName: null,
      };
    }

    const slice = enriched.filter((s) => s.ms >= startMs && s.ms < endMs);
    const okCount = slice.filter((x) => x.suit.level === 'OK').length;
    const warnCount = slice.filter((x) => x.suit.level === 'WARN').length;

    const sh = new Date(startMs).getHours();
    const eh = new Date(endMs).getHours();
    let prefBonus = 0;
    let preferredName = null;

    for (const p of PREFERRED_WINDOWS) {
      const overlaps =
        (sh >= p.start && sh < p.end) ||
        (eh > p.start && eh <= p.end) ||
        (sh <= p.start && eh >= p.end);
      if (overlaps) {
        prefBonus = 3;
        preferredName = p.name;
        break;
      }
    }

    const level = okCount >= 2 ? 'OK' : warnCount >= 1 ? 'WARN' : 'WARN';

    return {
      kind: 'window',
      level,
      startMs,
      endMs,
      windowText: `${fmtTime(startMs)}–${fmtTime(endMs)}`,
      score: prefBonus + okCount * 2 + warnCount * 1,
      reason:
        level === 'OK'
          ? 'Aman: tidak ada hujan 3–6 jam setelah semprot'
          : 'Waspada: cek angin/kondisi lapangan',
      preferredName,
    };
  });

  scored.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    return a.startMs - b.startMs;
  });

  return scored[0];
}

function levelLabel(level) {
  if (level === 'OK') return 'OK';
  if (level === 'WARN') return 'Waspada';
  return 'Tunda';
}
function levelGlyph(level) {
  if (level === 'OK') return 'weather-ok';
  if (level === 'WARN') return 'weather-warn';
  return 'weather-hold';
}

/* ===================== Screen ===================== */

export default function DashboardScreen({ route, navigation }) {
  const token = route?.params?.token;
  const user = route?.params?.user;
  const { signOut } = useContext(AuthContext);
  const { bottomInset, contentPadding, contentWidthStyle, isPhoneLike, isTiny, isWide } = useResponsive();

  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [errorText, setErrorText] = useState('');

  // Weather
  const [wxSlots, setWxSlots] = useState([]);
  const [wxLoading, setWxLoading] = useState(false);
  const [wxErr, setWxErr] = useState('');
  const [wxDaily, setWxDaily] = useState([]);
  const [wxDailyLoading, setWxDailyLoading] = useState(false);
  const [wxDailyErr, setWxDailyErr] = useState('');
  const [wxModal, setWxModal] = useState(false);

  const intervalRef = useRef(null);
  const appState = useRef(AppState.currentState);
  const mountedRef = useRef(true);
  const lastWeatherAlertKeyRef = useRef(null);

  const authHdr = { Authorization: `Bearer ${token}` };
  const formatIDR = (n) => `Rp ${Number(n || 0).toLocaleString('id-ID')}`;

  // Confirm logout
  const confirmLogout = () => {
    const doLogout = async () => {
      try { await signOut(); } catch {}
    };
    if (Platform.OS === 'web') {
      if (window.confirm('Yakin ingin logout?')) doLogout();
      return;
    }
    Alert.alert('Konfirmasi', 'Yakin ingin logout?', [
      { text: 'Batal', style: 'cancel' },
      { text: 'Logout', style: 'destructive', onPress: doLogout },
    ]);
  };

  useLayoutEffect(() => {
    navigation.setOptions({
      headerShown: !isPhoneLike,
      headerRight: isPhoneLike ? undefined : () => (
          <TouchableOpacity
            style={s.logoutBtn}
            onPress={confirmLogout}
            accessibilityRole="button"
            accessibilityLabel="Logout"
            hitSlop={{ top: 8, right: 8, bottom: 8, left: 8 }}
            activeOpacity={0.85}
          >
            <View style={s.logoutInner}>
              <PadiProIcon name="logout" size={13} color={PADIPRO.danger} strokeWidth={2} />
              <Text style={s.logoutText}>Logout</Text>
            </View>
          </TouchableOpacity>
        ),
      headerTitle: 'Dashboard',
    });
    // Only rerun when the layout class changes; adding changing function
    // identities here can retrigger setOptions too often in React Navigation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPhoneLike]);

  const fetchSummary = async ({ silent = false } = {}) => {
    const controller = new AbortController();
    try {
      if (!silent) setLoading(true);
      setErrorText('');

      const r = await axios.get(`${API_BASE}/dashboard/summary`, {
        headers: authHdr,
        signal: controller.signal,
      });

      if (!mountedRef.current) return;
      setSummary(r.data);
    } catch (e) {
      if (!mountedRef.current) return;
      if (axios.isCancel?.(e) || e.name === 'CanceledError' || e.message === 'canceled') return;

      const msg = e?.response?.data?.error || e?.message || 'Gagal memuat dashboard';
      setErrorText(msg);
      if (Platform.OS === 'web') console.error(msg);
      else Alert.alert('Error', msg);
    } finally {
      if (!mountedRef.current) return;
      setLoading(false);
      setRefreshing(false);
    }
    return () => controller.abort();
  };

  const fetchWeather = async ({ silent = false } = {}) => {
    const controller = new AbortController();
    try {
      if (!silent) setWxLoading(true);
      setWxErr('');

      const adm4 = user?.adm4 || DEFAULT_ADM4;
      const r = await axios.get(
        `https://api.bmkg.go.id/publik/prakiraan-cuaca?adm4=${encodeURIComponent(adm4)}`,
        { signal: controller.signal }
      );

      if (!mountedRef.current) return;

      const slots = normalizeBmkgSlots(r.data);
      if (!slots.length) {
        setWxSlots([]);
        setWxErr('Data BMKG kosong');
        return;
      }
      setWxSlots(slots);
    } catch (e) {
      if (!mountedRef.current) return;
      if (axios.isCancel?.(e) || e.name === 'CanceledError' || e.message === 'canceled') return;
      setWxErr('Gagal ambil BMKG');
      if (Platform.OS === 'web') console.error(e);
    } finally {
      if (!mountedRef.current) return;
      setWxLoading(false);
    }
    return () => controller.abort();
  };

  const fetchDailyForecast = async ({ silent = false } = {}) => {
    const controller = new AbortController();
    try {
      if (!silent) setWxDailyLoading(true);
      setWxDailyErr('');

      const { lat, lon } = getForecastCoords(user);
      const r = await axios.get('https://api.open-meteo.com/v1/forecast', {
        params: {
          latitude: lat,
          longitude: lon,
          daily: [
            'weather_code',
            'precipitation_sum',
            'precipitation_probability_max',
            'wind_speed_10m_max',
            'wind_gusts_10m_max',
          ].join(','),
          timezone: 'Asia/Jakarta',
          forecast_days: FORECAST_DAYS,
        },
        signal: controller.signal,
      });

      if (!mountedRef.current) return;

      const days = normalizeDailyForecast(r.data);
      if (!days.length) {
        setWxDaily([]);
        setWxDailyErr('Prakiraan 14 hari kosong');
        return;
      }
      setWxDaily(days);
    } catch (e) {
      if (!mountedRef.current) return;
      if (axios.isCancel?.(e) || e.name === 'CanceledError' || e.message === 'canceled') return;
      setWxDailyErr('Gagal ambil prakiraan 14 hari');
      if (Platform.OS === 'web') console.error(e);
    } finally {
      if (!mountedRef.current) return;
      setWxDailyLoading(false);
    }
    return () => controller.abort();
  };

  useEffect(() => {
    mountedRef.current = true;
    fetchSummary();
    fetchWeather();
    fetchDailyForecast();
    return () => { mountedRef.current = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto refresh
  useEffect(() => {
    intervalRef.current = setInterval(() => {
      fetchSummary({ silent: true });
      fetchWeather({ silent: true });
    }, AUTO_REFRESH_MS);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  // Refresh when focused
  useFocusEffect(
    React.useCallback(() => {
      fetchSummary({ silent: true });
      fetchWeather({ silent: true });
      fetchDailyForecast({ silent: true });
      return () => {};
    }, [])
  );

  // Refresh when app active
  useEffect(() => {
    const sub = AppState.addEventListener('change', (next) => {
      const prev = appState.current;
      appState.current = next;
      if (prev?.match(/inactive|background/) && next === 'active') {
        fetchSummary({ silent: true });
        fetchWeather({ silent: true });
        fetchDailyForecast({ silent: true });
      }
    });
    return () => sub.remove();
  }, []);

  const onRefresh = async () => {
    setRefreshing(true);
    await Promise.all([fetchSummary(), fetchWeather(), fetchDailyForecast()]);
  };

  // Build today + tomorrow advice + range suhu
  const wxSummary = useMemo(() => {
    const now = Date.now();
    const todayKey = fmtDayKey(now);
    const tomorrowKey = fmtDayKey(now + 24 * 3600000);

    const today = pickBestWindowForDay(wxSlots, todayKey);
    const tomorrow = pickBestWindowForDay(wxSlots, tomorrowKey);

    const tempsToday = wxSlots
      .filter((s) => fmtDayKey(s.ms) === todayKey && s.tempC != null)
      .map((s) => s.tempC);

    const todayRange =
      tempsToday.length > 0
        ? `${Math.round(Math.min(...tempsToday))}–${Math.round(Math.max(...tempsToday))}`
        : '—';

    return { todayKey, tomorrowKey, today, tomorrow, todayRange };
  }, [wxSlots]);

  const wxExtremeAlert = useMemo(() => buildExtremeWeatherAlert(wxSlots), [wxSlots]);
  const wxDailyExtremeAlert = useMemo(() => buildDailyForecastAlert(wxDaily), [wxDaily]);
  const wxActiveAlert = wxExtremeAlert || wxDailyExtremeAlert;

  useEffect(() => {
    if (!wxActiveAlert || wxLoading || wxDailyLoading) return;
    if (wxActiveAlert === wxExtremeAlert && wxErr) return;
    if (wxActiveAlert === wxDailyExtremeAlert && wxDailyErr) return;
    if (lastWeatherAlertKeyRef.current === wxActiveAlert.key) return;

    lastWeatherAlertKeyRef.current = wxActiveAlert.key;
    Alert.alert(wxActiveAlert.title, wxActiveAlert.message, [
      { text: 'Lihat detail', onPress: () => setWxModal(true) },
      { text: 'OK' },
    ]);
  }, [wxActiveAlert, wxExtremeAlert, wxDailyExtremeAlert, wxLoading, wxDailyLoading, wxErr, wxDailyErr]);

  // UI bits
  const statCardWidth = isTiny ? '48.5%' : (isWide ? '23.5%' : '47.5%');
  const quickActionWidth = isTiny ? '48.5%' : (isWide ? '23%' : '47%');

  const StatCard = ({ label, value, highlight, footnote, accentColor }) => (
    <View style={[s.card, isTiny && s.cardPhone, { width: statCardWidth }, highlight && s.cardHighlight]}>
      <View style={[s.cardAccentLine, { backgroundColor: accentColor || PADIPRO.primary }]} />
      <View style={s.cardTopRow}>
        <Text style={[s.cardLabel, highlight && s.cardLabelHighlight]} numberOfLines={1}>
          {label}
        </Text>
        <View
          style={[
            s.cardDot,
            { backgroundColor: highlight ? PADIPRO.gold : (accentColor || PADIPRO.primary) },
          ]}
        />
      </View>
      <Text
        style={[s.cardValue, isTiny && s.cardValuePhone, highlight && s.cardValueHighlight]}
        numberOfLines={1}
        adjustsFontSizeToFit
        minimumFontScale={0.7}
      >
        {value}
      </Text>
      {!!footnote && (
        <Text style={[s.cardFootnote, isTiny && s.cardFootnotePhone, highlight && s.cardFootnoteHighlight]} numberOfLines={1}>
          {footnote}
        </Text>
      )}
    </View>
  );

  const Skeleton = () => (
    <View style={[s.card, isTiny && s.cardPhone, { width: statCardWidth }]}>
      <View style={[s.skelLine, { width: '42%' }]} />
      <View style={[s.skelLine, { width: '72%', height: 26, marginTop: 12 }]} />
      <View style={[s.skelLine, { width: '58%', marginTop: 12 }]} />
    </View>
  );

  const pillLevel = wxErr ? 'WARN' : (wxSummary?.today?.level || 'WARN');
  const pillBorder =
    pillLevel === 'OK' ? s.pillOk : pillLevel === 'WARN' ? s.pillWarn : s.pillHold;

  const locLabel = user?.weather_label || DEFAULT_LOC_LABEL;
  const lowStockCount = Number(summary?.low_stock_count || 0);
  const hasLowStock = lowStockCount > 0;
  const forecastRangeText = wxDaily.length
    ? `${shortDayLabel(wxDaily[0].ms)}–${shortDayLabel(wxDaily[wxDaily.length - 1].ms)}`
    : 'Mulai hari ini';
  const forecastHoldCount = wxDaily.filter((day) => day?.risk?.level === 'HOLD').length;
  const forecastWarnCount = wxDaily.filter((day) => day?.risk?.level === 'WARN').length;
  const forecastFirstRisk = wxDaily.find((day) => day?.risk?.level === 'HOLD' || day?.risk?.level === 'WARN');
  const todayDateText = new Date().toLocaleDateString('id-ID', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
  const quickActions = [
    {
      key: 'inventory',
      icon: 'inventory',
      label: 'Inventori',
      sub: 'Pantau stok, masuk, dan keluar barang',
      route: 'Inventory',
      accent: PADIPRO.primary,
      iconBg: PADIPRO.primarySoft,
      iconBorder: PADIPRO.primarySoftStrong,
    },
    {
      key: 'expenses',
      icon: 'expenses',
      label: 'Expenses',
      sub: 'Kontrol biaya operasional harian',
      route: 'ExpensesList',
      accent: PADIPRO.goldDeep,
      iconBg: PADIPRO.goldSoft,
      iconBorder: `${PADIPRO.gold}40`,
    },
    {
      key: 'scanner',
      icon: 'scanner',
      label: 'Scanner',
      sub: 'Scan barcode produk lebih cepat',
      route: 'Scanner',
      accent: VIOLET,
      iconBg: `${VIOLET}18`,
      iconBorder: `${VIOLET}40`,
    },
    {
      key: 'ops',
      icon: 'operations',
      label: 'Operasional',
      sub: 'Catat aktivitas semprot lapangan',
      route: 'Operations',
      accent: PADIPRO.primary,
      iconBg: PADIPRO.primarySoft,
      iconBorder: PADIPRO.primarySoftStrong,
    },
    {
      key: 'tractor',
      icon: 'tractor',
      label: 'Usaha Traktor',
      sub: 'Atur pekerjaan, biaya, dan servis unit',
      route: 'TractorDashboard',
      accent: PADIPRO.goldDeep,
      iconBg: PADIPRO.goldSoft,
      iconBorder: `${PADIPRO.gold}40`,
    },
    {
      key: 'land-measure',
      icon: 'measure',
      label: 'Ukur Tanah',
      sub: 'Ukur luas dan rante langsung dengan GPS',
      route: 'LandMeasure',
      accent: PADIPRO.sky,
      iconBg: PADIPRO.skySoft,
      iconBorder: `${PADIPRO.sky}40`,
    },
    {
      key: 'buku-sawah',
      icon: 'calendar',
      label: 'Buku Sawah',
      sub: 'Kalender perkembangan padi per lahan',
      route: 'BukuSawah',
      accent: PADIPRO.sky,
      iconBg: PADIPRO.skySoft,
      iconBorder: `${PADIPRO.sky}40`,
    },
    {
      key: 'monitoring',
      icon: 'monitoring',
      label: 'Monitoring',
      sub: 'Checklist air, daun, dan foto sawah',
      route: 'Monitoring',
      accent: PADIPRO.sky,
      iconBg: PADIPRO.skySoft,
      iconBorder: `${PADIPRO.sky}40`,
    },
    {
      key: 'report',
      icon: 'report',
      label: 'Report Panen',
      sub: 'Analisis hasil dan performa sawah',
      route: 'Report',
      accent: PADIPRO.goldDeep,
      iconBg: PADIPRO.goldSoft,
      iconBorder: `${PADIPRO.gold}40`,
    },
    {
      key: 'harvest',
      icon: 'harvest',
      label: 'Hasil Panen',
      sub: 'Rekap produksi dari tiap lahan',
      route: 'Harvests',
      accent: PADIPRO.goldDeep,
      iconBg: PADIPRO.goldSoft,
      iconBorder: `${PADIPRO.gold}40`,
    },
  ];

  const ForecastDayCard = ({ day }) => {
    const level = day?.risk?.level || 'WARN';
    const cardStyle = level === 'OK' ? s.forecastOk : level === 'WARN' ? s.forecastWarn : s.forecastHold;
    const riskStyle = level === 'HOLD' ? s.forecastRiskHoldPill : level === 'WARN' ? s.forecastRiskWarnPill : s.forecastRiskOkPill;
    const riskTextStyle = level === 'HOLD' ? s.forecastRiskHoldText : level === 'WARN' ? s.forecastRiskWarnText : s.forecastRiskOkText;

    return (
      <View style={[s.forecastDayCard, cardStyle]}>
        <View style={s.forecastCardTop}>
          <View style={{ flex: 1, minWidth: 0 }}>
            <Text style={s.forecastDayText} numberOfLines={1}>{shortDayLabel(day.ms)}</Text>
            <Text style={s.forecastDescText} numberOfLines={1}>{day.desc}</Text>
          </View>
          <View style={[s.forecastRiskPill, riskStyle]}>
            <Text style={[s.forecastRiskPillText, riskTextStyle]} numberOfLines={1}>
              {levelLabel(level)}
            </Text>
          </View>
        </View>
        <View style={s.forecastMetricGrid}>
          <View style={s.forecastMetricCell}>
            <Text style={s.forecastMetricValue} numberOfLines={1}>{day.rainMm != null ? day.rainMm : '—'}</Text>
            <Text style={s.forecastMetricLabel} numberOfLines={1}>mm</Text>
          </View>
          <View style={s.forecastMetricCell}>
            <Text style={s.forecastMetricValue} numberOfLines={1}>{day.rainProb != null ? day.rainProb : '—'}</Text>
            <Text style={s.forecastMetricLabel} numberOfLines={1}>%</Text>
          </View>
          <View style={s.forecastMetricCell}>
            <Text style={s.forecastMetricValue} numberOfLines={1}>{day.gustKmh != null ? day.gustKmh : '—'}</Text>
            <Text style={s.forecastMetricLabel} numberOfLines={1}>hbs</Text>
          </View>
        </View>
        <Text style={s.forecastRiskText} numberOfLines={1}>
          {day.risk?.label || 'Pantau'}
        </Text>
      </View>
    );
  };

  const ForecastStrip = () => (
    <>
      {!!wxDailyErr && (
        <View style={s.forecastError}>
          <Text style={s.forecastErrorText}>{wxDailyErr}</Text>
        </View>
      )}
      {wxDailyLoading && !wxDaily.length ? (
        <View style={s.forecastLoading}>
          <ActivityIndicator color={PADIPRO.primary} />
          <Text style={s.forecastLoadingText}>Memuat prakiraan 14 hari…</Text>
        </View>
      ) : (
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={s.forecastList}
        >
          {wxDaily.length ? (
            wxDaily.map((day) => <ForecastDayCard key={day.dateKey} day={day} />)
          ) : (
            <View style={s.noSlots}>
              <Text style={s.noSlotsText}>Prakiraan 14 hari belum tersedia.</Text>
            </View>
          )}
        </ScrollView>
      )}
    </>
  );

  const DayPanel = ({ title, dayKeyValue, advice }) => {
    const slots = wxSlots.filter((s) => fmtDayKey(s.ms) === dayKeyValue);

    const badgeLevel = advice?.level || 'HOLD';
    const badgeStyle = badgeLevel === 'OK' ? s.badgeOk : badgeLevel === 'WARN' ? s.badgeWarn : s.badgeHold;
    const boxStyle = badgeLevel === 'OK' ? s.boxOk : badgeLevel === 'WARN' ? s.boxWarn : s.boxHold;

    return (
      <View style={s.dayCard}>
        <View style={s.dayHeader}>
          <View style={{ flex: 1 }}>
            <Text style={s.dayTitle}>{title}</Text>
            <Text style={s.daySub}>{locLabel} • slot 3 jam BMKG</Text>
          </View>
          <View style={[s.dayBadge, badgeStyle]}>
            <Text style={s.dayBadgeText}>{levelLabel(badgeLevel)}</Text>
          </View>
        </View>

        <View style={[s.dayBox, boxStyle]}>
          <Text style={s.dayBoxTop}>
            Window rekomendasi:{' '}
            <Text style={{ fontWeight: '900' }}>{advice?.windowText || '—'}</Text>
          </Text>
          <Text style={s.dayBoxSub}>{advice?.reason || '—'}</Text>
        </View>

        <Text style={s.slotsTitle}>Prakiraan per 3 jam</Text>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ gap: 10, paddingVertical: 8 }}>
          {slots.length ? (
            slots.map((slot) => {
              const suit = slotSuitability(slot);
              const extreme = classifyExtremeWeatherSlot(slot);
              const chipStyle = suit.level === 'OK' ? s.chipOk : suit.level === 'WARN' ? s.chipWarn : s.chipHold;
              return (
                <View key={slot.ms} style={[s.chip, chipStyle]}>
                  <Text style={s.chipTop}>
                    {fmtTime(slot.ms)} • {slot.tempC != null ? `${slot.tempC}°C` : '—'}
                  </Text>
                  <Text style={s.chipSub} numberOfLines={1}>{slot.desc}</Text>
                  <Text style={s.chipMeta} numberOfLines={1}>
                    Angin: {slot.windKmh != null ? `${slot.windKmh} km/j` : '—'} • RH: {slot.hum != null ? `${slot.hum}%` : '—'}
                  </Text>
                  <Text style={s.chipMeta} numberOfLines={1}>
                    Hujan: {slot.precipMm != null ? `${slot.precipMm} mm` : '—'}
                  </Text>
                  {!!extreme && (
                    <Text style={s.chipDangerText} numberOfLines={1}>Ekstrem: {extreme.label}</Text>
                  )}
                  {suit.level !== 'OK' && (
                    <Text style={s.chipWarnText} numberOfLines={1}>{suit.reason}</Text>
                  )}
                </View>
              );
            })
          ) : (
            <View style={s.noSlots}>
              <Text style={s.noSlotsText}>Tidak ada data slot.</Text>
            </View>
          )}
        </ScrollView>
      </View>
    );
  };

  return (
    <>
      <ScrollView
        style={s.screen}
        contentInsetAdjustmentBehavior="automatic"
        contentContainerStyle={[
          s.wrap,
          isTiny && s.wrapPhone,
          { padding: contentPadding, paddingBottom: 30 + bottomInset },
          contentWidthStyle,
        ]}
        showsVerticalScrollIndicator={false}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={PADIPRO.primary} />}
      >
        <View style={[s.headerBlock, isTiny && s.headerBlockPhone]}>
          <View style={s.heroGoldBar} />
          <View style={s.heroGlowOne} />
          <View style={s.heroGlowTwo} />
          <EnterpriseHeader
            transparent
            title={`Halo, ${user?.name || 'Mitra Tani'}`}
            subtitle="PanenKu Pusat Kendali Tani Nusantara"
            right={(
              <View style={s.heroActions}>
                <TouchableOpacity
                  style={s.heroHeaderIcon}
                  onPress={onRefresh}
                  activeOpacity={0.85}
                  hitSlop={{ top: 6, right: 6, bottom: 6, left: 6 }}
                  accessibilityRole="button"
                  accessibilityLabel="Refresh dashboard"
                >
                  <PadiProIcon name="operations" size={16} color="#FFFFFF" strokeWidth={2} />
                </TouchableOpacity>
                {isPhoneLike && (
                  <TouchableOpacity
                    style={[s.heroHeaderIcon, s.heroLogoutIcon]}
                    onPress={confirmLogout}
                    activeOpacity={0.85}
                    hitSlop={{ top: 6, right: 6, bottom: 6, left: 6 }}
                    accessibilityRole="button"
                    accessibilityLabel="Logout"
                  >
                    <PadiProIcon name="logout" size={15} color="#FFFFFF" strokeWidth={2} />
                  </TouchableOpacity>
                )}
              </View>
            )}
          />
          <Text style={s.heroDate}>{todayDateText}</Text>

          <TouchableOpacity
            style={[s.wxChip, pillBorder]}
            activeOpacity={0.9}
            onPress={() => setWxModal(true)}
            accessibilityRole="button"
            accessibilityLabel="Prediksi cuaca"
          >
            <View style={s.wxChipRow}>
              <View style={{ flex: 1 }}>
                <Text style={s.wxChipBig} numberOfLines={1}>
                  {wxLoading ? '—' : (wxSummary?.todayRange ? `${wxSummary.todayRange}°C` : '—')}
                </Text>
                <Text style={s.wxChipSub} numberOfLines={1}>
                  Cuaca hari ini • {wxLoading ? 'Memuat…' : (wxErr ? 'Belum sinkron' : levelLabel(wxSummary?.today?.level))}
                </Text>
              </View>

              <View style={s.wxChipIconWrap}>
                <PadiProIcon
                  name={wxLoading || wxErr ? 'weather-warn' : levelGlyph(wxSummary?.today?.level)}
                  size={20}
                  color={pillLevel === 'HOLD' ? PADIPRO.danger : pillLevel === 'OK' ? PADIPRO.primary : PADIPRO.goldDeep}
                  strokeWidth={1.9}
                />
              </View>
            </View>
          </TouchableOpacity>

          {!!wxActiveAlert && (
            <TouchableOpacity
              style={s.weatherAlertBanner}
              activeOpacity={0.9}
              onPress={() => setWxModal(true)}
              accessibilityRole="button"
              accessibilityLabel="Peringatan cuaca ekstrem"
            >
              <View style={s.weatherAlertIcon}>
                <PadiProIcon name="weather-hold" size={17} color={PADIPRO.danger} strokeWidth={2} />
              </View>
              <View style={{ flex: 1 }}>
                <Text style={s.weatherAlertTitle} numberOfLines={1}>
                  {wxActiveAlert.headline}
                </Text>
                <Text style={s.weatherAlertText} numberOfLines={2}>
                  {wxActiveAlert.shortText}. Padi keluar malai rawan rebah.
                </Text>
              </View>
            </TouchableOpacity>
          )}

          <TouchableOpacity
            style={s.landSummaryMini}
            onPress={() => navigation.navigate('Operations', { token, user })}
            activeOpacity={0.88}
            accessibilityRole="button"
            accessibilityLabel="Buka data lahan aktif"
          >
            <View style={s.landSummaryIcon}>
              <PadiProIcon name="measure" size={17} color={PADIPRO.goldSoft} strokeWidth={2} />
            </View>
            <View style={s.landSummaryMain}>
              <Text style={s.landSummaryEyebrow}>PORTOFOLIO LAHAN</Text>
              <View style={s.landSummaryValueRow}>
                <Text style={s.landSummaryCount}>{Number(summary?.total_fields || 0).toLocaleString('id-ID')}</Text>
                <Text style={s.landSummaryCountLabel}>lahan aktif</Text>
              </View>
            </View>
            <View style={s.landSummaryDivider} />
            <View style={s.landSummaryArea}>
              <Text style={s.landSummaryAreaValue}>
                {Number(summary?.total_area_rante || 0).toLocaleString('id-ID', { maximumFractionDigits: 2 })}
              </Text>
              <Text style={s.landSummaryAreaLabel}>total rante</Text>
            </View>
            <Text style={s.landSummaryArrow}>›</Text>
          </TouchableOpacity>

          <View style={s.heroMetaRow}>
            <View style={[s.metaPill, s.metaPillSafe]}>
              <Text style={s.metaPillText} numberOfLines={1}>Lokasi BMKG: {locLabel}</Text>
            </View>
            <View style={[s.metaPill, hasLowStock ? s.metaPillWarn : s.metaPillSafe]}>
              <Text style={[s.metaPillText, hasLowStock && s.metaPillWarnText]} numberOfLines={1}>
                {hasLowStock ? `${lowStockCount} item low stock` : 'Semua stok aman'}
              </Text>
            </View>
          </View>

          {!!errorText && (
            <View style={s.alert}>
              <Text style={s.alertText} numberOfLines={2}>
                {errorText}
              </Text>
            </View>
          )}
        </View>

        <View style={s.forecastSection}>
          <View style={s.forecastHeaderRow}>
            <View style={{ flex: 1, minWidth: 0 }}>
              <Text style={s.forecastEyebrow}>Cuaca 14 hari</Text>
              <Text style={s.forecastSub} numberOfLines={1}>
                Open-Meteo • {locLabel} • {forecastRangeText}
              </Text>
            </View>
            <TouchableOpacity
              style={s.forecastRefreshBtn}
              onPress={() => fetchDailyForecast()}
              activeOpacity={0.9}
              accessibilityRole="button"
              accessibilityLabel="Refresh prakiraan 14 hari"
            >
              <Text style={s.forecastRefreshText}>{wxDailyLoading ? '...' : 'Segarkan'}</Text>
            </TouchableOpacity>
          </View>
          <View style={s.forecastSummaryRow}>
            <View style={[s.forecastSummaryPill, forecastHoldCount ? s.forecastSummaryDanger : s.forecastSummarySafe]}>
              <Text style={s.forecastSummaryText} numberOfLines={1}>
                {forecastHoldCount
                  ? `${forecastHoldCount} hari ekstrem`
                  : forecastWarnCount
                    ? `${forecastWarnCount} hari waspada`
                    : 'Tidak ada ekstrem'}
              </Text>
            </View>
            <View style={s.forecastSummaryPill}>
              <Text style={s.forecastSummaryText} numberOfLines={1}>
                {forecastFirstRisk ? `Pantau ${shortDayLabel(forecastFirstRisk.ms)}` : 'Stabil'}
              </Text>
            </View>
          </View>
          <ForecastStrip />
          <Text style={s.forecastNote} numberOfLines={2}>
            Sumber non-BMKG untuk pembanding; pantau ulang saat mendekati tanggal semprot atau fase keluar malai.
          </Text>
        </View>

        <Text style={s.sectionTitle}>Ringkasan Utama</Text>
        <View style={s.grid}>
          {loading ? (
            <>
              <Skeleton /><Skeleton /><Skeleton /><Skeleton />
            </>
          ) : (
            <>
              <StatCard
                label="Total Produk"
                value={summary?.total_products ?? '—'}
                footnote="Jenis produk aktif"
                accentColor={PADIPRO.primary}
              />
              <StatCard
                label="Total Stok"
                value={summary?.total_stock ?? '—'}
                footnote="Unit siap pakai"
                accentColor={PADIPRO.primary}
              />
              <StatCard
                label="Low Stock"
                value={summary?.low_stock_count ?? '—'}
                highlight={Number(summary?.low_stock_count) > 0}
                footnote={Number(summary?.low_stock_count) > 0 ? 'Prioritas restock' : 'Stok kritis terkendali'}
                accentColor={PADIPRO.goldDeep}
              />
              <StatCard
                label="Pengeluaran Bulan Ini"
                value={formatIDR(summary?.expenses_this_month)}
                footnote="Akurasi kontrol biaya"
                accentColor={PADIPRO.goldDeep}
              />
            </>
          )}
        </View>

        <View style={s.controlCard}>
          <View style={s.controlRow}>
            <Text style={s.controlTitle}>Panel Prioritas Hari Ini</Text>
            <View style={[s.controlBadge, hasLowStock ? s.controlBadgeWarn : s.controlBadgeSafe]}>
              <Text style={s.controlBadgeText}>{hasLowStock ? 'Butuh Aksi' : 'Stabil'}</Text>
            </View>
          </View>
          <Text style={s.controlSub}>
            {hasLowStock
              ? `${lowStockCount} item perlu restock untuk menjaga operasional tetap lancar.`
              : 'Tidak ada stok kritis. Fokus pada efisiensi aplikasi di lapangan.'}
          </Text>
          <View style={s.controlDivider} />
          <View style={s.controlMetricRow}>
            <Text style={s.controlMetricLabel}>Anggaran berjalan</Text>
            <Text style={s.controlMetricValue}>{formatIDR(summary?.expenses_this_month)}</Text>
          </View>
        </View>

        <Text style={s.sectionTitle}>Aksi Cepat Lapangan</Text>

        <View style={s.actionsRow}>
          {quickActions.map((item) => (
            <TouchableOpacity
              key={item.key}
              style={[s.quickBtn, isTiny && s.quickBtnPhone, { width: quickActionWidth }]}
              onPress={() => navigation.navigate(item.route, { token, user })}
              activeOpacity={0.9}
              accessibilityRole="button"
              accessibilityLabel={item.label}
            >
              <View style={[s.quickAccentLine, { backgroundColor: item.accent }]} />
              <View style={[s.quickIconWrap, isTiny && s.quickIconWrapPhone, { backgroundColor: item.iconBg, borderColor: item.iconBorder }]}>
                <PadiProIcon name={item.icon} size={19} color={item.accent} strokeWidth={1.9} />
              </View>
              <Text style={[s.quickLabel, isTiny && s.quickLabelPhone]} numberOfLines={1}>{item.label}</Text>
              <Text style={[s.quickSub, isTiny && s.quickSubPhone]} numberOfLines={isTiny ? 1 : 2}>{item.sub}</Text>
            </TouchableOpacity>
          ))}
        </View>

        <View style={s.actionColumn}>
          <TouchableOpacity
            style={s.primaryBtn}
            onPress={() => navigation.navigate('AddExpense', { token })}
            activeOpacity={0.9}
          >
            <Text style={s.primaryBtnText}>+ Catat Pengeluaran</Text>
            <Text style={s.primaryBtnSub}>Input biaya sawah hari ini secara cepat</Text>
          </TouchableOpacity>
        </View>

        {!loading && !summary && (
          <View style={s.emptyBox}>
            <ActivityIndicator />
            <Text style={s.emptyText}>Belum ada data. Tarik ke bawah untuk refresh.</Text>
          </View>
        )}

        <View style={{ height: 16 }} />
      </ScrollView>

      {/* Modal detail cuaca */}
      <Modal
        visible={wxModal}
        transparent
        animationType="fade"
        onRequestClose={() => setWxModal(false)}
      >
        <View style={s.modalBackdrop}>
          <View style={[s.modalCard, contentWidthStyle]}>
            <View style={s.modalHeader}>
              <View style={{ flex: 1 }}>
                <Text style={s.modalTitle}>Prediksi Cuaca & Saran Semprot</Text>
                <Text style={s.modalSub}>
                  {locLabel} • BMKG • rule: tunda jika hujan 3–6 jam setelah semprot
                </Text>
              </View>

              <TouchableOpacity
                onPress={() => setWxModal(false)}
                style={s.modalCloseBtn}
                activeOpacity={0.85}
              >
                <Text style={s.modalCloseText}>Tutup</Text>
              </TouchableOpacity>
            </View>

            {!!wxErr && (
              <View style={s.modalAlert}>
                <Text style={s.modalAlertText}>{wxErr}</Text>
              </View>
            )}

            <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={{ paddingBottom: 10 }}>
              <DayPanel
                title={`Hari ini • ${dayLabel(Date.now())}`}
                dayKeyValue={wxSummary?.todayKey}
                advice={wxSummary?.today}
              />
              <DayPanel
                title={`Besok • ${dayLabel(Date.now() + 24 * 3600000)}`}
                dayKeyValue={wxSummary?.tomorrowKey}
                advice={wxSummary?.tomorrow}
              />

              <View style={s.modalDailyBlock}>
                <View style={s.forecastHeaderRow}>
                  <View style={{ flex: 1, minWidth: 0 }}>
                    <Text style={s.slotsTitle}>Prakiraan 14 hari</Text>
                    <Text style={s.forecastSub} numberOfLines={1}>
                      Open-Meteo • {forecastRangeText} • hujan, angin, dan hembusan
                    </Text>
                  </View>
                </View>
                <ForecastStrip />
              </View>

              <TouchableOpacity
                style={s.refreshBtn}
                onPress={() => Promise.all([fetchWeather(), fetchDailyForecast()])}
                activeOpacity={0.9}
              >
                <Text style={s.refreshBtnText}>
                  {wxLoading || wxDailyLoading ? 'Memuat…' : 'Refresh Cuaca'}
                </Text>
              </TouchableOpacity>
            </ScrollView>
          </View>
        </View>
      </Modal>
    </>
  );
}

const s = StyleSheet.create({
  screen: { backgroundColor: PADIPRO.bg },
  wrap: { padding: SPACE.lg, paddingBottom: 30, gap: SPACE.lg },
  wrapPhone: { gap: 14 },

  headerBlock: {
    position: 'relative',
    borderRadius: RADIUS.xl + 4,
    backgroundColor: PADIPRO.primary,
    borderWidth: 1,
    borderColor: PADIPRO.primaryDark,
    padding: SPACE.lg,
    overflow: 'hidden',
    ...padiproShadowLg,
  },
  headerBlockPhone: {
    borderRadius: 22,
    padding: 13,
  },
  heroGoldBar: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    width: 4,
    backgroundColor: PADIPRO.gold,
  },
  heroGlowOne: {
    position: 'absolute',
    width: 190,
    height: 190,
    borderRadius: 95,
    backgroundColor: 'rgba(166,124,0,0.20)',
    top: -84,
    right: -28,
  },
  heroGlowTwo: {
    position: 'absolute',
    width: 120,
    height: 120,
    borderRadius: 60,
    backgroundColor: 'rgba(255,255,255,0.06)',
    bottom: -38,
    left: -18,
  },
  heroHeaderIcon: {
    width: 44,
    height: 44,
    borderRadius: 999,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.16)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.38)',
  },
  heroActions: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  heroLogoutIcon: {
    backgroundColor: 'rgba(159,33,56,0.58)',
    borderColor: 'rgba(255,255,255,0.28)',
  },
  heroDate: { marginTop: 10, color: '#D9E5D9', fontWeight: '700' },

  wxChip: {
    marginTop: 12,
    borderRadius: RADIUS.xl - 2,
    paddingVertical: 11,
    paddingHorizontal: 12,
    borderWidth: 1.5,
    backgroundColor: PADIPRO.surface,
    ...padiproShadow,
  },
  wxChipRow: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  wxChipBig: {
    fontSize: 22,
    fontWeight: '900',
    color: PADIPRO.textStrong,
    lineHeight: 24,
    fontVariant: ['tabular-nums'],
  },
  wxChipSub: { marginTop: 2, fontSize: 12, fontWeight: '700', color: PADIPRO.muted },
  wxChipIconWrap: {
    width: 34,
    height: 34,
    borderRadius: 17,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: PADIPRO.primarySoft,
    borderWidth: 1,
    borderColor: PADIPRO.border,
  },
  weatherAlertBanner: {
    marginTop: 10,
    borderRadius: RADIUS.lg,
    paddingVertical: 10,
    paddingHorizontal: 11,
    borderWidth: 1,
    borderColor: 'rgba(159,33,56,0.44)',
    backgroundColor: PADIPRO.dangerSoft,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    ...padiproShadow,
  },
  weatherAlertIcon: {
    width: 34,
    height: 34,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(159,33,56,0.12)',
    borderWidth: 1,
    borderColor: 'rgba(159,33,56,0.28)',
  },
  weatherAlertTitle: { color: PADIPRO.danger, fontWeight: '900', fontSize: 13 },
  weatherAlertText: { marginTop: 3, color: PADIPRO.text, fontWeight: '700', fontSize: 12, lineHeight: 16 },

  pillOk: { borderColor: PADIPRO.primary },
  pillWarn: { borderColor: PADIPRO.gold, backgroundColor: PADIPRO.goldSoft },
  pillHold: { borderColor: PADIPRO.danger, backgroundColor: PADIPRO.dangerSoft },

  heroMetaRow: { marginTop: 12, flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  metaPill: {
    paddingHorizontal: 10,
    paddingVertical: 7,
    borderRadius: 999,
    borderWidth: 1,
    backgroundColor: 'rgba(255,255,255,0.15)',
    borderColor: 'rgba(255,255,255,0.24)',
    maxWidth: '100%',
  },
  landSummaryMini: {
    marginTop: 11,
    minHeight: 62,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingHorizontal: 11,
    paddingVertical: 9,
    borderRadius: 15,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.20)',
    backgroundColor: 'rgba(255,255,255,0.10)',
  },
  landSummaryIcon: {
    width: 35,
    height: 35,
    borderRadius: 11,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(205,169,70,0.18)',
    borderWidth: 1,
    borderColor: 'rgba(235,211,143,0.30)',
  },
  landSummaryMain: { flex: 1, minWidth: 0 },
  landSummaryEyebrow: { color: '#C8D9CF', fontSize: 8.5, fontWeight: '900', letterSpacing: 1 },
  landSummaryValueRow: { flexDirection: 'row', alignItems: 'baseline', gap: 5, marginTop: 2 },
  landSummaryCount: { color: '#FFFFFF', fontSize: 19, lineHeight: 21, fontWeight: '900', fontVariant: ['tabular-nums'] },
  landSummaryCountLabel: { color: '#E4EEE8', fontSize: 11, fontWeight: '700' },
  landSummaryDivider: { width: 1, height: 32, backgroundColor: 'rgba(255,255,255,0.16)' },
  landSummaryArea: { minWidth: 66, alignItems: 'flex-end' },
  landSummaryAreaValue: { color: '#FFF0BF', fontSize: 18, lineHeight: 20, fontWeight: '900', fontVariant: ['tabular-nums'] },
  landSummaryAreaLabel: { color: '#C8D9CF', fontSize: 9.5, fontWeight: '700', marginTop: 2 },
  landSummaryArrow: { color: 'rgba(255,255,255,0.62)', fontSize: 23, fontWeight: '500', marginLeft: -3 },
  metaPillSafe: {},
  metaPillWarn: { backgroundColor: 'rgba(166,124,0,0.24)', borderColor: 'rgba(166,124,0,0.6)' },
  metaPillText: { color: '#EAF1E8', fontWeight: '700', fontSize: 12 },
  metaPillWarnText: { color: PADIPRO.goldSoft },

  alert: {
    marginTop: 10,
    backgroundColor: 'rgba(159,33,56,0.28)',
    borderColor: 'rgba(255,255,255,0.35)',
    borderWidth: 1,
    padding: 10,
    borderRadius: 12,
  },
  alertText: { color: PADIPRO.dangerSoft, fontWeight: '700' },

  sectionTitle: { fontWeight: '900', color: PADIPRO.textStrong, letterSpacing: 0.3, fontSize: 16 },

  forecastSection: {
    marginTop: -4,
  },
  forecastHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
  },
  forecastEyebrow: {
    color: PADIPRO.textStrong,
    fontWeight: '900',
    fontSize: 14,
    lineHeight: 18,
  },
  forecastSub: {
    marginTop: 2,
    color: PADIPRO.muted,
    fontWeight: '700',
    fontSize: 11,
  },
  forecastRefreshBtn: {
    minHeight: 44,
    paddingHorizontal: 10,
    borderRadius: RADIUS.sm,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: PADIPRO.border,
    backgroundColor: PADIPRO.primarySoft,
  },
  forecastRefreshText: {
    color: PADIPRO.primaryDark,
    fontWeight: '900',
    fontSize: 11,
  },
  forecastSummaryRow: {
    marginTop: 9,
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 7,
  },
  forecastSummaryPill: {
    minHeight: 30,
    paddingHorizontal: 9,
    paddingVertical: 6,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: PADIPRO.border,
    backgroundColor: PADIPRO.surface,
  },
  forecastSummarySafe: {
    borderColor: PADIPRO.primarySoftStrong,
    backgroundColor: PADIPRO.primarySoft,
  },
  forecastSummaryDanger: {
    borderColor: 'rgba(159,33,56,0.34)',
    backgroundColor: PADIPRO.dangerSoft,
  },
  forecastSummaryText: {
    color: PADIPRO.text,
    fontWeight: '900',
    fontSize: 11,
  },
  forecastList: {
    gap: 8,
    paddingTop: 9,
    paddingBottom: 6,
  },
  forecastDayCard: {
    width: 146,
    minHeight: 124,
    borderRadius: RADIUS.md,
    borderWidth: 1,
    padding: 10,
    backgroundColor: PADIPRO.surface,
    ...padiproShadow,
  },
  forecastOk: { borderColor: PADIPRO.primary, backgroundColor: PADIPRO.primarySoft },
  forecastWarn: { borderColor: PADIPRO.gold, backgroundColor: PADIPRO.goldSoft },
  forecastHold: { borderColor: PADIPRO.danger, backgroundColor: PADIPRO.dangerSoft },
  forecastCardTop: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  },
  forecastRiskPill: {
    minWidth: 42,
    minHeight: 24,
    paddingHorizontal: 7,
    borderRadius: 999,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
  },
  forecastRiskOkPill: {
    borderColor: PADIPRO.primarySoftStrong,
    backgroundColor: PADIPRO.primarySoft,
  },
  forecastRiskWarnPill: {
    borderColor: `${PADIPRO.gold}55`,
    backgroundColor: 'rgba(255,255,255,0.45)',
  },
  forecastRiskHoldPill: {
    borderColor: 'rgba(159,33,56,0.28)',
    backgroundColor: 'rgba(255,255,255,0.46)',
  },
  forecastRiskPillText: {
    fontWeight: '900',
    fontSize: 9.5,
  },
  forecastRiskOkText: { color: PADIPRO.primaryDark },
  forecastRiskWarnText: { color: PADIPRO.goldDeep },
  forecastRiskHoldText: { color: PADIPRO.danger },
  forecastMetricGrid: {
    marginTop: 10,
    flexDirection: 'row',
    gap: 6,
  },
  forecastMetricCell: {
    flex: 1,
    minHeight: 38,
    borderRadius: RADIUS.sm,
    borderWidth: 1,
    borderColor: 'rgba(18,32,26,0.08)',
    backgroundColor: 'rgba(255,255,255,0.42)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  forecastMetricValue: {
    color: PADIPRO.textStrong,
    fontWeight: '900',
    fontSize: 12,
    fontVariant: ['tabular-nums'],
  },
  forecastMetricLabel: {
    marginTop: 1,
    color: PADIPRO.muted,
    fontWeight: '800',
    fontSize: 9,
  },
  forecastDayText: {
    color: PADIPRO.textStrong,
    fontWeight: '900',
    fontSize: 12,
  },
  forecastDescText: {
    marginTop: 2,
    color: PADIPRO.muted,
    fontWeight: '800',
    fontSize: 10.5,
  },
  forecastRiskText: {
    marginTop: 8,
    color: PADIPRO.text,
    fontWeight: '900',
    fontSize: 10.5,
  },
  forecastError: {
    marginTop: 9,
    borderRadius: RADIUS.md,
    borderWidth: 1,
    borderColor: 'rgba(159,33,56,0.3)',
    backgroundColor: PADIPRO.dangerSoft,
    padding: 9,
  },
  forecastErrorText: { color: PADIPRO.danger, fontWeight: '800', fontSize: 12 },
  forecastLoading: {
    minHeight: 86,
    marginTop: 9,
    borderRadius: RADIUS.md,
    borderWidth: 1,
    borderColor: PADIPRO.border,
    backgroundColor: PADIPRO.surface,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  forecastLoadingText: { color: PADIPRO.muted, fontWeight: '800', fontSize: 12 },
  forecastNote: {
    marginTop: 0,
    color: PADIPRO.muted,
    fontWeight: '700',
    fontSize: 10.5,
    lineHeight: 14,
  },

  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
    marginTop: -2,
  },
  card: {
    borderRadius: RADIUS.lg,
    borderWidth: 1,
    borderColor: PADIPRO.border,
    backgroundColor: PADIPRO.surface,
    paddingVertical: 12,
    paddingHorizontal: 12,
    ...padiproShadow,
  },
  cardPhone: {
    minHeight: 116,
    paddingVertical: 11,
    paddingHorizontal: 11,
  },
  cardHighlight: { borderColor: PADIPRO.gold, backgroundColor: PADIPRO.goldSoft },
  cardAccentLine: {
    height: 4,
    borderRadius: 999,
    marginBottom: 10,
  },
  cardTopRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 6 },
  cardLabel: { color: PADIPRO.muted, fontWeight: '700', fontSize: 13, flex: 1 },
  cardLabelHighlight: { color: PADIPRO.goldDeep },
  cardDot: { width: 9, height: 9, borderRadius: 4.5 },
  cardValue: {
    marginTop: 10,
    fontSize: 24,
    fontWeight: '900',
    color: PADIPRO.textStrong,
    fontVariant: ['tabular-nums'],
  },
  cardValuePhone: {
    fontSize: 21,
    lineHeight: 25,
  },
  cardValueHighlight: { color: PADIPRO.goldDeep },
  cardFootnote: { marginTop: 8, color: PADIPRO.muted, fontWeight: '600', fontSize: 12 },
  cardFootnotePhone: { marginTop: 6, fontSize: 11 },
  cardFootnoteHighlight: { color: PADIPRO.goldDeep },

  controlCard: {
    borderRadius: RADIUS.xl,
    backgroundColor: PADIPRO.surface,
    borderWidth: 1,
    borderColor: PADIPRO.border,
    padding: 14,
    ...padiproShadow,
  },
  controlRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 10 },
  controlTitle: { fontWeight: '900', color: PADIPRO.textStrong, fontSize: 15, flex: 1 },
  controlBadge: { paddingHorizontal: 10, paddingVertical: 6, borderRadius: 999 },
  controlBadgeWarn: { backgroundColor: PADIPRO.goldSoft },
  controlBadgeSafe: { backgroundColor: PADIPRO.primarySoft },
  controlBadgeText: { fontWeight: '800', color: PADIPRO.primaryDark, fontSize: 12 },
  controlSub: { marginTop: 10, color: PADIPRO.muted, fontWeight: '600', lineHeight: 19 },
  controlDivider: { marginTop: 12, height: 1, backgroundColor: PADIPRO.border },
  controlMetricRow: { marginTop: 12, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  controlMetricLabel: { color: PADIPRO.muted, fontWeight: '700' },
  controlMetricValue: {
    color: PADIPRO.primaryDark,
    fontWeight: '900',
    fontSize: 18,
    fontVariant: ['tabular-nums'],
  },

  actionsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'flex-start',
    gap: 10,
    marginTop: -2,
  },
  quickBtn: {
    width: '47%',
    borderRadius: RADIUS.lg,
    paddingVertical: 12,
    paddingHorizontal: 12,
    minHeight: 118,
    borderWidth: 1,
    borderColor: PADIPRO.border,
    backgroundColor: PADIPRO.surface,
    overflow: 'hidden',
    ...padiproShadow,
  },
  quickBtnPhone: {
    minHeight: 112,
    paddingVertical: 11,
    paddingHorizontal: 11,
  },
  quickAccentLine: {
    height: 4,
    borderRadius: 999,
    marginBottom: 10,
  },
  quickIconWrap: {
    width: 38,
    height: 38,
    borderRadius: 10,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  quickIconWrapPhone: {
    width: 36,
    height: 36,
  },
  quickLabel: { marginTop: 9, fontWeight: '800', color: PADIPRO.textStrong },
  quickLabelPhone: { fontSize: 13 },
  quickSub: { marginTop: 4, color: PADIPRO.muted, fontWeight: '600', fontSize: 12, lineHeight: 16 },
  quickSubPhone: { fontSize: 11, lineHeight: 14 },

  actionColumn: { marginTop: -2 },
  primaryBtn: {
    borderRadius: RADIUS.lg,
    paddingVertical: 14,
    paddingHorizontal: 14,
    alignItems: 'center',
    minHeight: 54,
    borderWidth: 1,
    borderColor: PADIPRO.primaryDark,
    backgroundColor: PADIPRO.primary,
    ...padiproShadowMd,
  },
  primaryBtnText: {
    color: '#F8FFF6',
    fontWeight: '900',
    letterSpacing: 0.25,
    fontSize: 16,
  },
  primaryBtnSub: { marginTop: 4, color: '#D7E8CF', fontWeight: '600', fontSize: 12 },

  skelLine: { height: 12, backgroundColor: PADIPRO.surfaceSunken, borderRadius: 6 },

  emptyBox: { alignItems: 'center', marginTop: 12 },
  emptyText: { color: PADIPRO.muted, marginTop: 8, fontWeight: '600' },

  logoutBtn: {
    marginRight: 14,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 10,
    backgroundColor: PADIPRO.dangerSoft,
    cursor: 'pointer',
  },
  logoutInner: { flexDirection: 'row', alignItems: 'center', gap: 5 },
  logoutText: { color: PADIPRO.danger, fontWeight: '800' },

  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(18,32,26,0.58)',
    padding: 16,
    justifyContent: 'center',
  },
  modalCard: {
    backgroundColor: PADIPRO.surface,
    borderRadius: RADIUS.xl - 2,
    padding: 14,
    maxHeight: '85%',
    borderWidth: 1,
    borderColor: PADIPRO.border,
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
  },
  modalTitle: { fontSize: 16, fontWeight: '900', color: PADIPRO.textStrong },
  modalSub: { marginTop: 4, color: PADIPRO.muted, fontWeight: '700', fontSize: 12 },
  modalCloseBtn: {
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderRadius: 12,
    backgroundColor: PADIPRO.surfaceSunken,
  },
  modalCloseText: { fontWeight: '800', color: PADIPRO.text },
  modalAlert: {
    marginTop: 10,
    backgroundColor: PADIPRO.dangerSoft,
    borderWidth: 1,
    borderColor: 'rgba(159,33,56,0.3)',
    padding: 10,
    borderRadius: 12,
  },
  modalAlertText: { color: PADIPRO.danger, fontWeight: '700' },

  modalDailyBlock: { marginTop: 2 },

  dayCard: {
    marginTop: 14,
    borderRadius: RADIUS.lg,
    borderWidth: 1,
    borderColor: PADIPRO.border,
    backgroundColor: PADIPRO.surface,
    padding: 12,
  },
  dayHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 10 },
  dayTitle: { fontWeight: '900', color: PADIPRO.textStrong },
  daySub: { marginTop: 3, color: PADIPRO.muted, fontWeight: '700', fontSize: 12 },
  dayBadge: { paddingHorizontal: 10, paddingVertical: 6, borderRadius: 999, borderWidth: 1 },
  dayBadgeText: { fontWeight: '900', color: PADIPRO.text, fontSize: 12 },
  badgeOk: { backgroundColor: PADIPRO.primarySoft, borderColor: PADIPRO.primary },
  badgeWarn: { backgroundColor: PADIPRO.goldSoft, borderColor: PADIPRO.gold },
  badgeHold: { backgroundColor: PADIPRO.dangerSoft, borderColor: PADIPRO.danger },
  dayBox: { marginTop: 10, borderRadius: RADIUS.md, borderWidth: 1, padding: 12 },
  dayBoxTop: { fontWeight: '800', color: PADIPRO.text },
  dayBoxSub: { marginTop: 4, color: PADIPRO.muted, fontWeight: '700' },
  boxOk: { backgroundColor: PADIPRO.primarySoft, borderColor: PADIPRO.primary },
  boxWarn: { backgroundColor: PADIPRO.goldSoft, borderColor: PADIPRO.gold },
  boxHold: { backgroundColor: PADIPRO.dangerSoft, borderColor: PADIPRO.danger },

  slotsTitle: { marginTop: 10, fontWeight: '900', color: PADIPRO.text },
  chip: { width: 228, borderRadius: RADIUS.md, borderWidth: 1, padding: 12 },
  chipTop: { fontWeight: '900', color: PADIPRO.text },
  chipSub: { marginTop: 4, color: PADIPRO.muted, fontWeight: '800' },
  chipMeta: { marginTop: 6, color: PADIPRO.text, fontWeight: '700', fontSize: 12 },
  chipWarnText: { marginTop: 6, color: PADIPRO.goldDeep, fontWeight: '800', fontSize: 12 },
  chipDangerText: { marginTop: 6, color: PADIPRO.danger, fontWeight: '900', fontSize: 12 },
  chipOk: { backgroundColor: PADIPRO.primarySoft, borderColor: PADIPRO.primary },
  chipWarn: { backgroundColor: PADIPRO.goldSoft, borderColor: PADIPRO.gold },
  chipHold: { backgroundColor: PADIPRO.dangerSoft, borderColor: PADIPRO.danger },

  noSlots: {
    padding: 12,
    borderRadius: RADIUS.md,
    borderWidth: 1,
    borderColor: PADIPRO.border,
    backgroundColor: PADIPRO.bg,
  },
  noSlotsText: { color: PADIPRO.muted, fontWeight: '700' },
  refreshBtn: {
    marginTop: 14,
    borderRadius: RADIUS.md,
    paddingVertical: 12,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: PADIPRO.border,
    backgroundColor: PADIPRO.primarySoft,
  },
  refreshBtnText: { fontWeight: '900', color: PADIPRO.text },
});
